/*
 * MM - Header v3 : comportements interactifs (menu mobile, sélecteur de
 * langue/devise, recolorisation au scroll quand le header est transparent).
 *
 * DEUX RÉGLAGES ORTHOGONAUX, que le code ne doit JAMAIS croiser :
 *  - `data-transparent` : le header se pose SUR la page (mode survol) et
 *    reprend son fond crème dès qu'on a quitté le haut ;
 *  - `data-sticky` : la pile d'en-tête reste à l'écran au défilement.
 *
 * Bug du 26/08 (Nolwenn) : `sticky_header: false` laissait le header
 * transparent à l'infini, parce que la recoloration était gardée par
 * `if (isSticky && isTransparent)`. Désactiver le suivi au scroll ne doit
 * retirer QUE le suivi au scroll. La recoloration ne lit plus `sticky`.
 *
 * Second défaut de la même famille : ce fichier est chargé par un
 * `<script src>` posé DANS la section. L'éditeur de thème remplace le
 * balisage de la section par `innerHTML`, ce qui ne ré-exécute jamais un
 * script externe : après une bascule de réglage, l'ancien câblage pointait
 * des nœuds détruits et la pile gardait `is-fixed` posé par le réglage
 * précédent (« je désactive sticky et il reste sticky »). D'où le couple
 * `unbindAll()` / `setup()` rejoué sur `shopify:section:load`.
 */
(function () {
  /* Seuil de recoloration, en pixels de défilement. Constante de maquette
     validée : le header reprend son fond dès qu'on décolle du haut. */
  var SOLID_AT = 50;

  /* Seuil anti-rebond du masquage mobile : absorbe l'élastique de Safari, qui
     sinon masque et rend l'en-tête en boucle sans que l'utilisateur défile. */
  var BOUNCE = 6;

  var listeners = [];

  function bind(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    listeners.push([target, type, handler, options]);
  }

  function unbindAll() {
    listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
    listeners = [];
    /* Le tiroir mobile verrouille le défilement du body : si l'éditeur
       re-rend la section pendant qu'il est ouvert, le verrou survivrait au
       nœud qui l'a posé et la page resterait bloquée. */
    document.body.style.overflow = '';
  }

  function setupScroll(header) {
    var isSticky = header.dataset.sticky === 'true';
    /* Comportement sur PETIT ÉCRAN, choisi dans l'éditeur (demande Nolwenn du
       26/08 : « que ça reste sticky sur mobile comme sur desktop, et un réglage
       pour décider »). Trois états, parce que trois existent réellement :
       `fixed` ne bouge jamais, `reveal` se masque en descendant et revient en
       remontant (remarque 29 d'Alexia du 24/08), `none` laisse la pile dans le
       flux. Le défaut est `fixed`, y compris quand l'attribut manque — un thème
       poussé avant ce réglage se comporte donc comme demandé, sans migration. */
    var mobileMode = header.dataset.stickyMobile || 'fixed';
    var isTransparent = header.dataset.transparent === 'true';
    var headerRoot = header.closest('.mm-header-v3');
    var stack = document.querySelector('[data-mm-header-stack]');
    var root = document.documentElement;

    if (stack) {
      stack.dataset.mmSticky = isSticky ? 'true' : 'false';
      if (!isSticky) {
        /* Remise à plat explicite, et pas seulement « on ne pose rien » : la
           pile vit dans le layout, l'éditeur ne la re-rend pas, elle garderait
           donc les classes et le cale posés quand sticky était encore actif. */
        stack.classList.remove('is-fixed', 'is-hidden');
        root.style.removeProperty('--mm-header-stack-height');
        root.style.removeProperty('--mm-header-stack-shift');
      }
    }

    var stackIsLive = !!stack && isSticky;
    if (!isTransparent && !stackIsLive) return;

    /*
     * Pile d'en-tête fixe : masquée au défilement vers le BAS, rendue au
     * défilement vers le HAUT (retour Alexia du 24/08, PDF V2, remarques 2 et 29).
     *
     * Deux mesures, et pas une constante, parce qu'aucune des deux n'est
     * déductible du CSS :
     *  - `height` est la place que la pile occupait dans le flux, que le cale
     *    reprend à l'identique pour qu'aucun pixel ne bouge au chargement ;
     *  - `shift` est la hauteur réellement PEINTE. En mode survol, le menu est
     *    `position: absolute` et déborde la boîte de la pile : masquer de la seule
     *    hauteur de boîte laisserait le menu à cheval sur la page.
     */
    /* Le seuil de bascule n'est PAS redéclaré ici : il est résolu par la media
       query de mm-header-stack.css et lu tel quel. Dupliquer la valeur en JS
       créerait une seconde source de vérité, qui dérive au premier changement
       de maquette. */
    var isDesktop = function () {
      return getComputedStyle(stack).getPropertyValue('--mm-header-mode').trim() === 'desktop';
    };

    var measure = function () {
      /* « Non fixe sur mobile » : la pile reste dans le flux, il n'y a donc ni
         cale à poser ni classe d'état à garder. Remise à plat explicite et non
         « on ne pose rien » — on arrive dans ce cas depuis le desktop par un
         simple redimensionnement, et les classes y auraient survécu. */
      if (!isDesktop() && mobileMode === 'none') {
        stack.classList.remove('is-fixed', 'is-hidden');
        root.style.removeProperty('--mm-header-stack-height');
        root.style.removeProperty('--mm-header-stack-shift');
        return;
      }

      /* Mesurer HORS position fixe : c'est la place que la pile occupe dans le
         flux qu'il faut reprendre, et elle n'est plus lisible une fois sortie. */
      var wasFixed = stack.classList.contains('is-fixed');
      if (wasFixed) stack.classList.remove('is-fixed');

      var flowHeight = stack.offsetHeight;
      var stackTop = stack.getBoundingClientRect().top;
      var painted = flowHeight;
      if (headerRoot) {
        painted = Math.max(painted, headerRoot.getBoundingClientRect().bottom - stackTop);
      }

      /* Sur <html> et non sur la pile : le cale est un FRÈRE de la pile, il
         n'hériterait pas d'une variable posée sur elle. */
      root.style.setProperty('--mm-header-stack-height', flowHeight + 'px');
      root.style.setProperty('--mm-header-stack-shift', Math.ceil(painted) + 'px');
      stack.classList.add('is-fixed');
    };

    var paintTransparency = function (y) {
      /* Ne lit QUE `transparent`. Voir l'en-tête de fichier : croiser les deux
         réglages ici est exactement le bug qu'on a payé. */
      headerRoot.classList.toggle('mm-header-v3--transparent', y <= SOLID_AT);
    };

    var lastY = window.scrollY;

    var paintStack = function (y) {
      var delta = y - lastY;
      /* Pas de garde pour le mode « défile » ici : `measure` a déjà retiré les
         trois classes d'état, et aucune des deux branches ci-dessous n'en repose
         quand la pile est dans le flux. Un second garde ne protégerait rien et
         donnerait deux endroits à tenir d'accord. */
      var desktop = isDesktop();

      if (desktop || mobileMode === 'fixed') {
        /* Le menu SUIT et ne se masque jamais : sur desktop (retour Alexia, PDF
           V2 remarque 2) et sur mobile en mode « fixe ». Il ne se CONDENSE pas
           non plus : retirer de la hauteur au défilement reflowait la page, et
           c'est le saut signalé au point 2 du PDF V3. Le gabarit du header ne
           dépend donc plus que des réglages, jamais de la position de scroll. */
        stack.classList.remove('is-hidden');
      } else if (y <= stack.offsetHeight || delta < -BOUNCE) {
        /* Sous la hauteur de la pile, on est encore « en haut » : jamais masquée. */
        stack.classList.remove('is-hidden');
      } else if (delta > BOUNCE) {
        /* Jamais masquer pendant que le tiroir mobile est ouvert : l'utilisateur
           perdrait le panneau qu'il est en train de lire. */
        var nav = document.getElementById('mm-header-v3-mobile-nav');
        if (!nav || !nav.classList.contains('is-open')) stack.classList.add('is-hidden');
      }
    };

    var ticking = false;

    /* Un SEUL abonnement au scroll pour les deux comportements : ils lisent la
       même valeur au même instant, deux écouteurs les feraient diverger d'une
       frame et doubleraient le coût sur un événement à haute fréquence. */
    var update = function () {
      ticking = false;
      var y = window.scrollY;
      if (isTransparent && headerRoot) paintTransparency(y);
      if (stackIsLive) paintStack(y);
      lastY = y;
    };

    var onScroll = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    if (stackIsLive) {
      /* Le cale reprend la hauteur que la pile occupait dans le flux. Depuis
         le retrait de la condensation (2026-09-02), cette hauteur ne varie plus
         au défilement : une seule mesure vaut pour toute la page. */
      measure();
      bind(window, 'resize', measure, { passive: true });
      /* `load` et non un ResizeObserver : la mesure bascule elle-même `is-fixed`,
         un observateur posé sur la pile se ré-armerait donc en boucle. Une reprise
         après le chargement des polices et des images suffit à couvrir le cas où
         la hauteur du bandeau bouge encore. */
      bind(window, 'load', measure);
    }

    bind(window, 'scroll', onScroll, { passive: true });
    update();
  }

  function setupMenus(header) {
    var burger = header.querySelector('[data-mm-burger]');
    var mobileNav = document.getElementById('mm-header-v3-mobile-nav');
    var closeBtn = mobileNav ? mobileNav.querySelector('[data-mm-close]') : null;
    var mobileCart = mobileNav ? mobileNav.querySelector('.mm-header-v3__mobile-cart') : null;
    var fondInerte = [];
    function openMenu() {
      if (!mobileNav) return;
      mobileNav.classList.add('is-open');
      mobileNav.setAttribute('aria-hidden', 'false');
      /* inert : menu fermé, ses liens sortaient quand même dans la tabulation alors qu'il
         est masqué aux lecteurs d'écran (audit « aria-hidden-focus », 27/09/2026). */
      mobileNav.removeAttribute('inert');
      burger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      /* Le focus restait sur la page, derrière le menu plein écran : il va sur « Fermer »
         (audit du 27/09/2026). Après le retrait de `inert`, le bouton est focalisable. */
      if (closeBtn) window.requestAnimationFrame(function () { closeBtn.focus({ preventScroll: true }); });
      /* Lecteurs d'écran : le menu plein écran est une fenêtre modale, la page derrière
         devient inerte le temps de son ouverture (comme le fait le tiroir panier). */
      mobileNav.setAttribute('role', 'dialog');
      mobileNav.setAttribute('aria-modal', 'true');
      if (!mobileNav.hasAttribute('aria-label')) mobileNav.setAttribute('aria-label', burger.getAttribute('aria-label') || 'Menu');
      fondInerte = Array.prototype.filter.call(
        document.querySelectorAll('#MainContent, .shopify-section-group-header-group, .shopify-section-group-footer-group'),
        function (el) { return !el.contains(mobileNav) && !el.hasAttribute('inert'); }
      );
      fondInerte.forEach(function (el) { el.setAttribute('inert', ''); });
    }

    function closeMenu(options) {
      if (!mobileNav) return;
      // Le focus revient au bouton Menu s'il était dans le menu (sinon il tomberait sur la page).
      var focusDansMenu = !(options && options.garderFocus) && mobileNav.contains(document.activeElement);
      fondInerte.forEach(function (el) { el.removeAttribute('inert'); });
      fondInerte = [];
      mobileNav.removeAttribute('role');
      mobileNav.removeAttribute('aria-modal');
      mobileNav.classList.remove('is-open');
      mobileNav.setAttribute('aria-hidden', 'true');
      mobileNav.setAttribute('inert', '');
      burger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (focusDansMenu && burger) burger.focus({ preventScroll: true });
    }

    if (burger) bind(burger, 'click', openMenu);
    if (closeBtn) bind(closeBtn, 'click', closeMenu);
    /* iPad tourné à l'horizontale, menu ouvert : l'en-tête passe en mode ordinateur, menu
       et bouton Menu disparaissent, mais la page restait inerte et bloquée (audit du
       27/09/2026). Plus de bouton Menu à l'écran = menu fermé. */
    bind(window, 'resize', function () {
      if (!mobileNav || !mobileNav.classList.contains('is-open') || !burger) return;
      if (burger.getBoundingClientRect().width === 0) closeMenu({ garderFocus: true });
    }, { passive: true });
    /* La fenêtre newsletter est une section de la page, donc dans #MainContent que le menu
       rend inerte : si elle s'ouvre (3 s) pendant que le menu est ouvert, le menu se ferme
       et lui laisse la main, sans lui reprendre le focus. */
    if (mobileNav && window.MutationObserver) {
      new MutationObserver(function () {
        if (mobileNav.classList.contains('is-open') && document.body.classList.contains('mm-newsletter-popup-open')) {
          closeMenu({ garderFocus: true });
        }
      }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
    if (mobileCart) {
      bind(mobileCart, 'click', function (event) {
        var cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer || typeof cartDrawer.open !== 'function') return;
        event.preventDefault();
        closeMenu();
        cartDrawer.open(mobileCart);
      });
    }

    if (mobileNav) {
      mobileNav.querySelectorAll('.mm-header-v3__nav-link').forEach(function (link) {
        bind(link, 'click', closeMenu);
      });
    }

    /* Localization dropdowns (currency / language) */
    var locales = header.querySelectorAll('[data-mm-locale]');

    function closeAll(except) {
      locales.forEach(function (loc) {
        if (loc === except) return;
        loc.classList.remove('is-open');
        var t = loc.querySelector('[data-mm-locale-toggle]');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    }

    locales.forEach(function (loc) {
      var toggle = loc.querySelector('[data-mm-locale-toggle]');
      if (!toggle) return;
      bind(toggle, 'click', function (e) {
        e.stopPropagation();
        var willOpen = !loc.classList.contains('is-open');
        closeAll(loc);
        loc.classList.toggle('is-open', willOpen);
        toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });
    });

    bind(document, 'click', function () { closeAll(null); });
    bind(document, 'keydown', function (e) {
      var menuOuvert = mobileNav && mobileNav.classList.contains('is-open');
      /* Une autre fenêtre par-dessus le menu (newsletter à 3 s, tiroir panier) garde la main
         sur Tab et Échap (audit du 27/09/2026). */
      if (menuOuvert && (document.body.classList.contains('mm-newsletter-popup-open') || document.querySelector('cart-drawer.is-open'))) return;
      /* Menu plein écran ouvert : Tab tourne dans le menu au lieu de partir vers la page
         cachée derrière (audit du 27/09/2026). */
      if (e.key === 'Tab' && menuOuvert) {
        var cibles = Array.prototype.filter.call(
          mobileNav.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'),
          function (el) { return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0; }
        );
        if (!cibles.length) return;
        var premier = cibles[0];
        var dernier = cibles[cibles.length - 1];
        var actif = document.activeElement;
        if (!mobileNav.contains(actif)) {
          e.preventDefault();
          (e.shiftKey ? dernier : premier).focus();
        } else if (e.shiftKey && actif === premier) {
          e.preventDefault();
          dernier.focus();
        } else if (!e.shiftKey && actif === dernier) {
          e.preventDefault();
          premier.focus();
        }
        return;
      }
      if (e.key !== 'Escape') return;
      closeAll(null);
      if (menuOuvert) {
        closeMenu();
        if (burger) burger.focus({ preventScroll: true });
      }
    });
  }

  function setup() {
    var header = document.getElementById('mm-header-v3');
    if (!header) return;
    setupScroll(header);
    setupMenus(header);
  }

  setup();

  /* Éditeur de thème : la section est remplacée par `innerHTML`, le
     `<script src>` qu'elle porte n'est jamais ré-exécuté. Cet écouteur est posé
     sur `document`, hors de la section, donc il survit au remplacement. */
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    if (!section || !section.querySelector || !section.querySelector('#mm-header-v3')) return;
    unbindAll();
    setup();
  });
})();
